import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency, campaignInfo } from "@/lib/format";

export const STATUS_META = {
  new: { label: "New", cls: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  confirmed: { label: "Confirmed", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  running: { label: "Running", cls: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
};

export const PAYMENT_META = {
  unpaid: { label: "Unpaid", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  partial: { label: "Partial", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return (
    <span data-testid="order-status-badge"
      className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium", m.cls)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />
      {m.label}
    </span>
  );
}

export function PaymentBadge({ status }) {
  const m = PAYMENT_META[status] || PAYMENT_META.unpaid;
  return (
    <span data-testid="payment-status-badge"
      className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium", m.cls)}>
      {m.label}
    </span>
  );
}

export function StatCard({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-600 bg-slate-100",
    blue: "text-blue-600 bg-blue-50",
    amber: "text-amber-600 bg-amber-50",
    sky: "text-sky-600 bg-sky-50",
    emerald: "text-emerald-600 bg-emerald-50",
    rose: "text-rose-600 bg-rose-50",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
        <span className={cn("h-9 w-9 rounded-lg grid place-items-center", tones[tone])}>
          {Icon && <Icon className="h-4.5 w-4.5" size={18} />}
        </span>
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900 font-display">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function CampaignProgress({ order, compact = false }) {
  const c = campaignInfo(order);
  if (!c.hasTarget && !c.totalDays) {
    return <p className="text-sm text-slate-500">No measurable campaign target set for this order.</p>;
  }
  return (
    <div data-testid="campaign-progress" className="space-y-4">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
        {c.totalDays > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Timeline</div>
            <div className="text-lg font-bold text-slate-900 font-display">Day {c.currentDay} of {c.totalDays}</div>
          </div>
        )}
        {c.hasTarget && (
          <>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Leads</div>
              <div className="text-lg font-bold text-slate-900 font-display">{c.generated} / {c.target}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Remaining</div>
              <div className="text-lg font-bold text-slate-900 font-display">{c.remaining}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Progress</div>
              <div className="text-lg font-bold text-blue-700 font-display">{c.leadPercent}%</div>
            </div>
          </>
        )}
      </div>
      {c.hasTarget && (
        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-blue-600 rounded-full transition-[width] duration-500"
            style={{ width: `${c.leadPercent}%` }} />
        </div>
      )}
      {!compact && c.totalDays > 0 && (
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-slate-400 rounded-full" style={{ width: `${c.dayPercent}%` }} />
        </div>
      )}
    </div>
  );
}

export function WhatsAppButton({ number, label = "Chat on WhatsApp", message = "" }) {
  const clean = (number || "").replace(/[^\d]/g, "");
  const href = `https://wa.me/${clean.length === 10 ? "91" + clean : clean}${message ? `?text=${encodeURIComponent(message)}` : ""}`;
  if (!clean) return null;
  return (
    <a data-testid="whatsapp-contact-button" href={href} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] text-white font-medium shadow-sm hover:bg-[#20bd5a] transition-colors text-sm">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.9-2-1-.3-.1-.4-.1-.6.1s-.7.9-.8 1.1c-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.6-1.5-.9-2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 00-8.6 15l-1.4 5 5.1-1.3A10 10 0 1012 2z"/></svg>
      {label}
    </a>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-20 text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {label}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <div className="text-center py-16 px-4">
      {Icon && <Icon className="h-10 w-10 mx-auto text-slate-300" />}
      <p className="mt-3 text-slate-700 font-medium">{title}</p>
      {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Money({ value }) {
  return <span className="font-mono">{formatCurrency(value)}</span>;
}
